import React from 'react';

import { css, Global } from '@emotion/react';
import { Box, Flex, List, ListItem, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

export type BulletPoint = {
  headline: string;
  content?: string;
};

interface IStyledUnorderedListProps {
  bulletPoints: BulletPoint[];
  title: string;
}

/**
 * Styled list of bullet points with lines connecting them.
 * storybook-check-ignore
 */
export const StyledUnorderedList: React.FC<IStyledUnorderedListProps> = React.memo(
  ({ bulletPoints, title }) => {
    const { isMobile } = useMobileDevice();
    return (
      <>
        <Flex flexDirection="column">
          <Flex flexDirection="column">
            <Text size={isMobile ? '160' : '150'} fontWeight="medium" mb={7}>
              {title}
            </Text>
            <Text size="100" mb={7} width={['100%', '100%', '100%', '100%']}>
              We recommend completing this as quickly as possible, so you don’t lose the home to
              another buyer. You can cancel any time and get 100% of your money back.
            </Text>
          </Flex>
          <Flex mx={4} width="100%">
            <List variant="unordered">
              {bulletPoints.map((bulletPoint, index) => (
                <ListItem key={index}>
                  <Box className="listHeader">
                    <Text fontWeight="semibold" size="120">
                      {bulletPoint.headline}
                    </Text>
                    {bulletPoint.content && (
                      <Text size="100" mt={4}>
                        {bulletPoint.content}
                      </Text>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Flex>
        </Flex>
        <Global
          styles={css`
            li {
              position: relative; /* so that pseudoelements are positioned relatively to their "li"s*/
              /* use padding-bottom instead of margin-bottom.*/
              margin-bottom: 0; /* This overrides previously specified margin-bottom */
              padding-bottom: 2.5em;
            }
            li:after {
              /* bullets */
              content: url('https://imgdrop.imgix.net/2023-06-1685735288399-26410.png');
              position: absolute;
              left: -26px; /*adjust manually*/
              top: 0px;
            }
            li:first-child:after {
              /* bullets */
              content: url('https://imgdrop.imgix.net/2023-06-1685729907650-90031.png');
              position: absolute;
              left: -26px; /*adjust manually*/
              top: 0px;
            }
            li:before {
              /* lines */
              content: '';
              position: absolute;
              left: -15px; /* adjust manually */
              border-left: 3px solid #dedede;
              height: 95%;
              width: 1px;
              top: 21px;
            }

            li:last-child:before {
              /* last li's line */
              height: 0px; /* shorten the line so it goes only up to the bullet. Is equal to first-child:before's top */
            }

            .listHeader {
              margin-top: 10px;
              position: relative;
              top: 0px;
              padding-left: 20px;
            }
          `}
        />
      </>
    );
  },
);

StyledUnorderedList.displayName = 'StyledUnorderedList';
